import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { statusService } from '../services/statusService';
import { taskService } from '../services/taskService';
import { adminService } from '../services/adminService';
import { userService } from '../services/userService';
import { useAuth } from '../context/AuthContext';
import Loading from '../components/common/Loading';
import StatusUpdatesTable from '../components/common/StatusUpdatesTable';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import InlineToast from '../components/common/InlineToast';

const TaskDetailsPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canViewDueDateRevisions = hasRole('Manager') || hasRole('Admin');
  const canManageGovernance = hasRole('Manager') || hasRole('Admin') || hasRole('Executive');
  const canEditTaskStatus = hasRole('Manager') || hasRole('Admin');

  const [task, setTask] = useState(null);
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [totalHoursSpent, setTotalHoursSpent] = useState(null);
  const [dueDateRevisions, setDueDateRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);
  const [revisionsError, setRevisionsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'info' });

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [editTask, setEditTask] = useState(null);
  const [initialTaskSnapshot, setInitialTaskSnapshot] = useState(null);

  const loadTask = useCallback(async () => {
    try {
      const response = await taskService.getTaskById(taskId);
      setTask(response.data || null);
    } catch (error) {
      console.error('Failed to load task:', error);
    }
  }, [taskId]);

  const loadTotalHours = useCallback(async () => {
    try {
      const response = await statusService.getStatusUpdates({ taskId });
      const total = (response.data || []).reduce((sum, u) => sum + parseFloat(u.timeSpentHours || 0), 0);
      setTotalHoursSpent(total);
    } catch (error) {
      console.error('Failed to load total hours:', error);
    }
  }, [taskId]);

  const loadTaskStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await statusService.getStatusUpdates({ taskId, startDate, endDate });
      setStatusUpdates(response.data || []);
    } catch (error) {
      console.error('Failed to load task status:', error);
    } finally {
      setLoading(false);
    }
  }, [taskId, startDate, endDate]);

  const loadDueDateRevisions = useCallback(async () => {
    try {
      setRevisionsLoading(true);
      setRevisionsError('');
      const response = await taskService.getDueDateRevisions(taskId);
      setDueDateRevisions(response.data || []);
    } catch (error) {
      setRevisionsError(error.response?.data?.message || 'Failed to load due date revisions');
    } finally {
      setRevisionsLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  useEffect(() => {
    loadTotalHours();
  }, [loadTotalHours]);

  useEffect(() => {
    loadTaskStatus();
  }, [loadTaskStatus]);

  useEffect(() => {
    if (!canViewDueDateRevisions) return;
    loadDueDateRevisions();
  }, [canViewDueDateRevisions, loadDueDateRevisions]);

  const loadReferenceData = async () => {
    try {
      const [clientsRes, categoriesRes, usersRes, tasksRes] = await Promise.all([
        adminService.getClients(),
        adminService.getCategories(),
        userService.getAllUsers(),
        taskService.getTasks()
      ]);
      setClients(clientsRes.data || []);
      setCategories(categoriesRes.data || []);
      setUsers(usersRes.data || []);
      setAllTasks(tasksRes.data || []);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  const isOverdue = (t) => {
    const effectiveDueDate = t.revisedDate || t.targetDate;
    if (!effectiveDueDate) return false;
    if (t.status === 'Completed') return false;
    const today = new Date();
    const target = new Date(effectiveDueDate);
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return target < today;
  };

  const handleOpenEdit = async () => {
    if (clients.length === 0) {
      await loadReferenceData();
    }
    const snapshot = {
      id: task.id,
      title: task.title || '',
      clientId: task.client?.id || '',
      categoryId: task.category?.id || '',
      hierarchyLevel: task.hierarchyLevel || 'epic',
      parentTaskId: task.parentTask?.id || '',
      priority: task.priority || 'P2',
      status: task.status || 'Not Started',
      targetDate: task.targetDate || '',
      revisedDate: task.revisedDate || '',
      size: task.size || 'M',
      customerId: task.customerId || '',
      externalLinkUrl: task.externalLinkUrl || '',
      externalLinkType: task.externalLinkType || '',
      assigneeId: task.assignee?.id || '',
      subAssigneeIds: task.subAssignees?.map(u => String(u.id)) || [],
      dueDateChangeReasonTag: '',
      dueDateChangeReasonNotes: '',
      originalTargetDate: task.targetDate || '',
      originalRevisedDate: task.revisedDate || ''
    };
    setEditTask(snapshot);
    setInitialTaskSnapshot(JSON.stringify({
      title: snapshot.title,
      clientId: String(snapshot.clientId),
      categoryId: String(snapshot.categoryId),
      hierarchyLevel: snapshot.hierarchyLevel,
      parentTaskId: String(snapshot.parentTaskId),
      priority: snapshot.priority,
      status: snapshot.status,
      targetDate: snapshot.targetDate,
      revisedDate: snapshot.revisedDate,
      size: snapshot.size,
      customerId: snapshot.customerId,
      externalLinkUrl: snapshot.externalLinkUrl,
      externalLinkType: snapshot.externalLinkType,
      assigneeId: String(snapshot.assigneeId),
      subAssigneeIds: snapshot.subAssigneeIds
    }));
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    const current = JSON.stringify({
      title: editTask.title || '',
      clientId: String(editTask.clientId || ''),
      categoryId: String(editTask.categoryId || ''),
      hierarchyLevel: editTask.hierarchyLevel || 'epic',
      parentTaskId: String(editTask.parentTaskId || ''),
      priority: editTask.priority || 'P2',
      status: editTask.status || 'Not Started',
      targetDate: editTask.targetDate || '',
      revisedDate: editTask.revisedDate || '',
      size: editTask.size || 'M',
      customerId: editTask.customerId || '',
      externalLinkUrl: editTask.externalLinkUrl || '',
      externalLinkType: editTask.externalLinkType || '',
      assigneeId: String(editTask.assigneeId || ''),
      subAssigneeIds: editTask.subAssigneeIds || []
    });
    if (initialTaskSnapshot && initialTaskSnapshot !== current) {
      if (!window.confirm('You have unsaved changes. Discard them?')) return;
    }
    setShowEditModal(false);
    setInitialTaskSnapshot(null);
    setEditTask(null);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      const dueDateChanged = editTask.revisedDate !== (editTask.originalRevisedDate || '');
      if (canManageGovernance && dueDateChanged && !editTask.dueDateChangeReasonTag) {
        setToast({ message: 'Revised date change reason is required.', type: 'error' });
        return;
      }

      const payload = {
        title: editTask.title,
        clientId: editTask.clientId,
        categoryId: editTask.categoryId || null,
        hierarchyLevel: editTask.hierarchyLevel,
        parentTaskId: editTask.hierarchyLevel === 'epic' ? null : (editTask.parentTaskId || null),
        priority: editTask.priority,
        status: editTask.status,
        targetDate: editTask.targetDate || null,
        revisedDate: editTask.revisedDate || null,
        size: editTask.size || null,
        customerId: editTask.customerId || null,
        assigneeId: editTask.assigneeId || null,
        subAssigneeIds: editTask.subAssigneeIds || [],
        externalLinkUrl: editTask.externalLinkUrl || null,
        externalLinkType: editTask.externalLinkUrl ? (editTask.externalLinkType || null) : null
      };
      if (dueDateChanged) {
        payload.dueDateChangeReasonTag = editTask.dueDateChangeReasonTag;
        payload.dueDateChangeReasonNotes = editTask.dueDateChangeReasonNotes || null;
      }

      await taskService.updateTask(task.id, payload);
      setShowEditModal(false);
      setInitialTaskSnapshot(null);
      setEditTask(null);
      setToast({ message: 'Task updated successfully.', type: 'success' });
      await loadTask();
      if (canViewDueDateRevisions) loadDueDateRevisions();
    } catch (error) {
      setToast({ message: error.response?.data?.message || 'Failed to update task.', type: 'error' });
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredUpdates = statusUpdates.filter(update => {
    const matchesSearch = !searchTerm ||
      update.user?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      update.statusText.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = !filterPriority || update.task?.priority === filterPriority;
    const matchesStatus = !filterStatus || update.status === filterStatus;
    return matchesSearch && matchesPriority && matchesStatus;
  });

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (endDate && value > endDate) setEndDate(value);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    if (startDate && value < startDate) setStartDate(value);
  };

  const applyDatePreset = (preset) => {
    const today = new Date();
    if (preset === 'today') {
      const value = today.toISOString().split('T')[0];
      setStartDate(value);
      setEndDate(value);
      return;
    }
    const days = preset === 'last7' ? 6 : 29;
    const start = new Date();
    start.setDate(today.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  const parentTaskOptions = allTasks
    .filter(t => {
      if (!editTask) return false;
      if (editTask.hierarchyLevel === 'story') return t.hierarchyLevel === 'epic';
      if (editTask.hierarchyLevel === 'subtask') return t.hierarchyLevel === 'story';
      return false;
    })
    .map(t => ({ value: t.id, label: t.title }));

  if (loading) return <Loading />;

  return (
    <div>
      {/* Header row: Back (left) | title | Edit (right) */}
      <div className="flex items-center justify-between mb-4">
        <Button onClick={() => navigate(-1)} variant="secondary">
          ← Back
        </Button>
        {canManageGovernance && (
          <Button onClick={handleOpenEdit} variant="primary">
            Edit Task
          </Button>
        )}
      </div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">{task?.title || 'Task details'}</h1>
        <p className="text-sm text-gray-600 mb-1">Assignee: {task?.assignee?.name || '-'}</p>
        <p className="text-sm text-gray-600 mb-1">
          Sub-Assignees: {task?.subAssignees?.map(user => user.name).join(', ') || '-'}
        </p>
        <p className="text-gray-600">All status updates for this task</p>
      </div>

      <div className="mb-4">
        <InlineToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'info' })}
        />
      </div>

      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Status:</span> <span className="font-medium">{task?.status || 'Not Started'}</span>
          </div>
          <div>
            <span className="text-gray-500">Target Date:</span> <span className="font-medium">{task?.targetDate || '-'}</span>
          </div>
          <div>
            <span className="text-gray-500">Revised Date:</span> <span className="font-medium">{task?.revisedDate || '-'}</span>
          </div>
          <div>
            <span className="text-gray-500">Size:</span> <span className="font-medium">{task?.size || '-'}</span>
          </div>
          <div>
            <span className="text-gray-500">Parent Task:</span> <span className="font-medium">{task?.parentTask?.title || '-'}</span>
          </div>
          <div>
            <span className="text-gray-500">Assignee:</span> <span className="font-medium">{task?.assignee?.name || '-'}</span>
          </div>
          <div>
            <span className="text-gray-500">Total Hours:</span>{' '}
            <span className="font-medium">
              {totalHoursSpent === null ? '...' : `${Number.isInteger(totalHoursSpent) ? totalHoursSpent : totalHoursSpent.toFixed(2)} h`}
            </span>
          </div>
          <div>
            <span className="text-gray-500">External Link:</span>{' '}
            {task?.externalLinkUrl ? (
              <a
                href={task.externalLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:underline"
              >
                {task?.externalLinkType ? `${task.externalLinkType.toUpperCase()} link` : 'Open link'}
              </a>
            ) : (
              <span className="font-medium">-</span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quick Presets</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => applyDatePreset('today')} className="px-3 py-2 text-sm border rounded hover:bg-gray-50">Today</button>
              <button type="button" onClick={() => applyDatePreset('last7')} className="px-3 py-2 text-sm border rounded hover:bg-gray-50">Last 7 days</button>
              <button type="button" onClick={() => applyDatePreset('last30')} className="px-3 py-2 text-sm border rounded hover:bg-gray-50">Last 30 days</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by user or progress..."
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="w-full border rounded px-3 py-2">
              <option value="">All Priorities</option>
              <option value="P1">P1 - Critical</option>
              <option value="P2">P2 - High</option>
              <option value="P3">P3 - Medium</option>
              <option value="P4">P4 - Low</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full border rounded px-3 py-2">
              <option value="">All Statuses</option>
              <option value="Not Started">Not Started</option>
              <option value="Planning">Planning</option>
              <option value="In Progress">In Progress</option>
              <option value="In Review">In Review</option>
              <option value="Blocked">Blocked</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredUpdates.length} of {statusUpdates.length} status updates
      </div>

      <StatusUpdatesTable updates={filteredUpdates} showUserColumn />

      {canViewDueDateRevisions && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-3">Due Date Revision History</h2>
          {revisionsLoading ? (
            <div className="bg-white p-4 rounded-lg shadow text-gray-600">Loading revision history...</div>
          ) : revisionsError ? (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{revisionsError}</div>
          ) : dueDateRevisions.length === 0 ? (
            <div className="bg-white p-4 rounded-lg shadow text-gray-600">No due date revisions found.</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Changed At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Old Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">New Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Changed By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dueDateRevisions.map((revision) => (
                    <tr key={revision.id}>
                      <td className="px-6 py-4 whitespace-nowrap">{formatDateTime(revision.changedAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{revision.oldDueDate || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{revision.newDueDate || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{revision.reasonTag}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {revision.changedBy?.name || revision.changedByUser?.name || (revision.changedByUser ? `${revision.changedByUser.firstName} ${revision.changedByUser.lastName}` : '-')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit Task Modal */}
      {editTask && (
        <Modal isOpen={showEditModal} onClose={handleCloseEdit} title="Edit Task">
          <form onSubmit={handleSubmitEdit}>
            <Input
              label="Task Title"
              value={editTask.title}
              onChange={(e) => setEditTask({ ...editTask, title: e.target.value })}
              required
            />

            <Select
              label="Client"
              value={editTask.clientId}
              onChange={(e) => setEditTask({ ...editTask, clientId: e.target.value })}
              options={clients.map(c => ({ value: c.id, label: c.name }))}
              placeholder="Select a client"
              required
            />

            <Select
              label="Category (Optional)"
              value={editTask.categoryId}
              onChange={(e) => setEditTask({ ...editTask, categoryId: e.target.value })}
              options={categories.map(c => ({ value: c.id, label: c.name }))}
              placeholder="Select a category"
            />

            <Select
              label="Assignee"
              value={editTask.assigneeId}
              onChange={(e) => setEditTask({ ...editTask, assigneeId: e.target.value })}
              options={users.map(u => ({ value: u.id, label: u.fullName || u.name || u.email || `User ${u.id}` }))}
              placeholder="Select an assignee"
              required
              disabled={!canManageGovernance}
            />

            <Select
              label="Sub-Assignees"
              name="subAssigneeIds"
              multiple
              value={editTask.subAssigneeIds}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions).map(o => o.value);
                setEditTask({ ...editTask, subAssigneeIds: values });
              }}
              options={users.map(u => ({ value: String(u.id), label: u.fullName || u.name || u.email || `User ${u.id}` }))}
            />

            <Select
              label="Priority"
              value={editTask.priority}
              onChange={(e) => setEditTask({ ...editTask, priority: e.target.value })}
              options={[
                { value: 'P1', label: 'P1 - Critical' },
                { value: 'P2', label: 'P2 - High' },
                { value: 'P3', label: 'P3 - Medium' },
                { value: 'P4', label: 'P4 - Low' }
              ]}
              required
              disabled={!canManageGovernance}
            />

            <Select
              label="Status"
              value={editTask.status}
              onChange={(e) => setEditTask({ ...editTask, status: e.target.value })}
              options={[
                { value: 'Not Started', label: 'Not Started' },
                { value: 'Planning', label: 'Planning' },
                { value: 'In Progress', label: 'In Progress' },
                { value: 'In Review', label: 'In Review' },
                { value: 'Blocked', label: 'Blocked' },
                { value: 'On Hold', label: 'On Hold' },
                { value: 'Completed', label: 'Completed' },
                { value: 'Cancelled', label: 'Cancelled' }
              ]}
              required
              disabled={!canEditTaskStatus}
            />

            <Input
              label="Target Date"
              type="date"
              value={editTask.targetDate}
              onChange={(e) => setEditTask({ ...editTask, targetDate: e.target.value })}
              disabled={!canManageGovernance}
            />

            <Input
              label="Revised Date"
              type="date"
              value={editTask.revisedDate}
              onChange={(e) => setEditTask({ ...editTask, revisedDate: e.target.value })}
              disabled={!canManageGovernance}
            />

            {canManageGovernance && isOverdue({ ...editTask }) && !editTask.revisedDate && (
              <p className="text-sm text-amber-700 mb-4">
                Task is overdue. Set a Revised Date and provide a change reason.
              </p>
            )}

            <Select
              label="Size"
              value={editTask.size}
              onChange={(e) => setEditTask({ ...editTask, size: e.target.value })}
              options={[
                { value: 'XS', label: 'XS' },
                { value: 'S', label: 'S' },
                { value: 'M', label: 'M' },
                { value: 'L', label: 'L' },
                { value: 'XL', label: 'XL' }
              ]}
              required
              disabled={!canManageGovernance}
            />

            <Select
              label="External Link Type (Optional)"
              value={editTask.externalLinkType}
              onChange={(e) => setEditTask({ ...editTask, externalLinkType: e.target.value })}
              options={[
                { value: 'github', label: 'GitHub' },
                { value: 'gitlab', label: 'GitLab' },
                { value: 'jira', label: 'JIRA' },
                { value: 'other', label: 'Other' }
              ]}
              placeholder="Select link type"
              disabled={!canManageGovernance}
            />

            <Input
              label="External Link URL (Optional)"
              type="url"
              value={editTask.externalLinkUrl}
              onChange={(e) => setEditTask({ ...editTask, externalLinkUrl: e.target.value })}
              placeholder="https://..."
              disabled={!canManageGovernance}
            />

            <Select
              label="Hierarchy Level"
              value={editTask.hierarchyLevel}
              onChange={(e) => setEditTask({ ...editTask, hierarchyLevel: e.target.value, parentTaskId: '' })}
              options={[
                { value: 'epic', label: 'Epic' },
                { value: 'story', label: 'Story' },
                { value: 'subtask', label: 'Subtask' }
              ]}
              required
            />

            {editTask.hierarchyLevel !== 'epic' && parentTaskOptions.length > 0 && (
              <Select
                label="Parent Task"
                value={editTask.parentTaskId}
                onChange={(e) => setEditTask({ ...editTask, parentTaskId: e.target.value })}
                options={parentTaskOptions}
                placeholder="Select parent task"
                required
                disabled={!canManageGovernance}
              />
            )}

            {canManageGovernance && editTask.revisedDate !== (editTask.originalRevisedDate || '') && (
              <div className="border rounded-lg p-4 bg-yellow-50 mb-4">
                <Select
                  label="Revised Date Change Reason"
                  value={editTask.dueDateChangeReasonTag}
                  onChange={(e) => setEditTask({ ...editTask, dueDateChangeReasonTag: e.target.value })}
                  options={[
                    { value: 'scope_change', label: 'Scope Change' },
                    { value: 'other_priorities', label: 'Other Priorities' },
                    { value: 'resource_constraint', label: 'Resource Constraint' },
                    { value: 'external_dependency', label: 'External Dependency' },
                    { value: 'other', label: 'Other' }
                  ]}
                  required
                />
                <Input
                  label="Reason Notes (Optional)"
                  value={editTask.dueDateChangeReasonNotes}
                  onChange={(e) => setEditTask({ ...editTask, dueDateChangeReasonNotes: e.target.value })}
                  placeholder="Additional context"
                />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={handleCloseEdit}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Update Task
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default TaskDetailsPage;
